import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import PatientUtils from 'services/PatientUtils';
import ProgressMessage from 'components/Utils/ProgressMessage';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DocumentDetails from 'components/HealthRecords/Documents/DocumentDetails';
import LargeResourcePreview from 'components/Utils/LargeResourcePreview';
import './style.scss';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DocumentListByCategory from 'components/HealthRecords/Documents/DocumentListByCategory';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import { DefaultCheckbox } from 'services/UIUtils';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import { DOCUMENT_TYPE_ARRAY } from 'components/HealthRecords/Documents/DocumentDetails/view';
import ToggleSwitch from 'components/Utils/ToggleSwitch';
import ContactsSearchModal from 'components/Modals/ContactsSearchModal';
import DefaultModal from 'components/Utils/DefaultModal';
import Icon from 'components/Utils/NewIcon';
import IconButton from '@material-ui/core/IconButton';
import DocumentBadge from 'components/HealthRecords/Documents/DocumentBadge';
import UnreadBadge from 'components/Common/UnreadBadge';
import PrescriptionDetails from 'components/HealthRecords/Prescriptions/PrescriptionDetails';
import PrescriptionBadge from 'components/HealthRecords/Prescriptions/PrescriptionBadge';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import StackItemsView from 'components/Common/StackItemsView';
import ReceiptBadge from 'components/Payments/ReceiptList/ReceiptBadge';
import moment from 'moment-timezone';
import { DocumentCheck } from 'components/HealthRecords/Documents/DocumentCheck';
import useDocumentSelection from 'hooks/useDocumentSelection';
import SelectableDocumentBadge from 'components/HealthRecords/Documents/SelectableDocumentBadge';

const OPTIONS = {
  fax: 'Fax',
  email: 'Email',
};

const AttachmentsTab = {
  add: 'Add Documents',
  reorder: 'Reorder Documents',
};

/**
 * @param {any} props
 */
const SendMultipleDocuments = (props) => {
  const dispatch = useAppDispatch();
  const { patient, onClose } = props;
  const patientId = patient.id;

  const templates = useAppSelector((state) => state.templates.templates);
  const patientHr = useAppSelector((state) => state.healthRecords.byId[patientId]);
  const patientReceipts = useAppSelector((state) => state.invoices.receipts[patientId]);
  const patientPrescriptions = patientHr?.prescriptions;
  const patientDocuments = patientHr?.documents;

  const defaultFax = useMemo(() => {
    let first = props.documents?.[0];
    if (!first?.template_id) return;
    return templates.find(({ id }) => id === first.template_id)?.fax;
  }, [props.documents, templates]);

  const { selected, setSelected, toggleDocument, isDocumentSelected, getDocumentIndex } =
    useDocumentSelection({
      initialSelected: props.documents || [],
      mode: 'object',
    });

  const [searchModal, setSearchModal] = useState(false);
  const [option, setOption] = useState(props.emailFirst ? OPTIONS.email : OPTIONS.fax);
  const [emailBody, setEmailBody] = useState(props.emailBody || '');
  const [subject, setSubject] = useState(props.emailSubject || '');
  const [emails, setEmails] = useState([props.email || '']);
  const [number, setNumber] = useState(props.fax || defaultFax || '');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [coverPage, setCoverPage] = useState(props.coverPage || '');
  const [document, setDocument] = useState((props.documents || [])[0]);
  const [saveCopy, setSaveCopy] = useState({ save: false });
  const [attachmentsTab, setAttachmentsTab] = useState(AttachmentsTab.add);

  useEffect(() => {
    setLoading(true);
    dispatch(api.HealthRecords.getCPP({ patientId }));
    setLoading(false);
  }, [patientId, dispatch]);

  const documents = useMemo(() => {
    return _.reverse(_.sortBy(patientDocuments, ['date']));
  }, [patientDocuments]);

  const prescriptions = useMemo(() => {
    return _.reverse(_.sortBy(patientPrescriptions, ['date']));
  }, [patientPrescriptions]);

  const receipts = useMemo(() => {
    return _.reverse(_.sortBy(patientReceipts, ['date']));
  }, [patientReceipts]);

  async function send() {
    const res = await (option === OPTIONS.fax ? sendFax() : sendEmail());
    props.onSend?.(option, res);
    return res;
  }

  async function sendFax() {
    setSending(true);

    let document = undefined;

    if (saveCopy.save) {
      document = {
        title: saveCopy.title || 'Copy From Fax',
        document_type: saveCopy.document_type,
        date: moment().unix(),
      };
    }

    let data = {
      document_ids: selected.map((document) => document.id),
      cover_page: coverPage,
      fax: number,
      document,
    };

    const result = await dispatch(
      api.Fax.faxMultipleDocuments({
        patientId,
        multipleDocumentPayload: data,
        faxMultipleDocumentPayload: data,
        documentId: props.documentId,
      }),
      {
        title: 'Fax Multiple Documents',
        successText: 'Fax queued successfully!',
      },
    );
    setSending();

    if (onClose && result) onClose();
  }

  async function sendEmail() {
    setSending(true);

    let multipleDocumentPayload = {
      document_ids: selected.map((document) => document.id),
      body: emailBody,
      subject,
      email: emails.filter((email) => !!email).join(','),
    };

    const result = await dispatch(
      api.Resource.sendPatientEmail({
        patientId,
        multipleDocumentPayload,
        emailMultipleDocumentPayload: multipleDocumentPayload,
        documentId: props.documentId,
      }),
      {
        title: 'Email Multiple Documents',
        successText: 'Email sent successfully!',
      },
    );

    setSending();

    if (onClose && result) onClose();
  }

  const hasEnteredEmails = !!emails.find((email) => !!email);

  const disabledButton =
    option === OPTIONS.fax ? !number : !hasEnteredEmails || !emailBody || !subject;

  return (
    <>
      {searchModal && (
        <DefaultModal
          key="consultant-search-modal"
          className="fit-modal"
          content={
            <ContactsSearchModal
              patient={patient}
              onSelect={(contact) => {
                let searchModalValue = searchModal;
                setSearchModal(false);
                if (option === OPTIONS.email && !!contact.email) {
                  let index = parseInt(searchModalValue);
                  let value = emails.map((email, emailIndex) => {
                    if (index === emailIndex) return contact.email;
                    return email;
                  });
                  setEmails(value);
                }
                if (option === OPTIONS.fax && contact.fax) setNumber(contact.fax);
              }}
            />
          }
          isOpen={!!searchModal}
          onRequestClose={(event) => {
            event?.stopPropagation();
            setSearchModal(false);
          }}
        />
      )}
      <div
        className="main-content send-multiple-documents"
        style={{ height: props.hideSend ? '100%' : 'calc(100% - 61px)' }}
      >
        <div className="attachment-list full-height underside-right-panel">
          {!props.hideToggle && (
            <div className="p-2">
              <ToggleSwitch
                option1={OPTIONS.fax}
                element1={OPTIONS.fax}
                option2={OPTIONS.email}
                element2={OPTIONS.email}
                onChange={setOption}
                toggled={option === OPTIONS.email}
              />
            </div>
          )}
          {!props.hideSend && (
            <div className="inputs">
              {option === OPTIONS.email && (
                <>
                  <div key={emails.length}>
                    {emails.map((email, savedIndex) => (
                      <div className="row contact" key={savedIndex}>
                        <DefaultTextField
                          name="email"
                          dataLpignore={true}
                          value={email}
                          onChange={(event) => {
                            let newValue = event.target.value;
                            setEmails(
                              emails.map((oldValue, index) => {
                                if (savedIndex === index) return newValue;
                                return oldValue;
                              }),
                            );
                          }}
                          label="Email address*"
                        />
                        {savedIndex > 0 && (
                          <IconButton
                            onClick={() => {
                              let value = [...emails];
                              value.splice(savedIndex, 1);
                              setEmails(value);
                            }}
                            className="red"
                          >
                            <Icon src="trash" className="white" showTooltip={true} />
                          </IconButton>
                        )}
                        {!emails.includes(patient.email) && (
                          <IconButton
                            onClick={() =>
                              setEmails((prevState) => {
                                let emails = [...prevState];
                                emails[savedIndex] = patient.email;
                                return emails;
                              })
                            }
                            className="blue"
                          >
                            <Icon
                              src="envelope"
                              className="white"
                              width="15px"
                              height="15px"
                              showTooltip={true}
                              alt="Add patient's email"
                            />
                          </IconButton>
                        )}
                        {savedIndex === emails.length - 1 && (
                          <IconButton onClick={() => setEmails([...emails, ''])} className="blue">
                            <Icon
                              src="plus"
                              className="white"
                              width="15px"
                              height="15px"
                              showTooltip={true}
                              alt="Add more emails"
                            />
                          </IconButton>
                        )}
                        {!props.hideContacts && (
                          <IconButton
                            onClick={() => setSearchModal('' + savedIndex)}
                            className="blue"
                          >
                            <Icon
                              src="magnifying-glass"
                              width="15px"
                              height="15px"
                              className="white"
                              alt="Search contacts"
                              showTooltip={true}
                            />
                          </IconButton>
                        )}
                      </div>
                    ))}
                  </div>
                  <DefaultTextField
                    name="subject"
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    label="Email subject*"
                  />
                  <span className="small-title mt-2">Email body*</span>
                  <TextareaAutosize
                    cacheMeasurements
                    name="email_body"
                    value={emailBody}
                    onChange={(event) => setEmailBody(event.target.value)}
                  />
                </>
              )}
              {option === OPTIONS.fax && (
                <>
                  <div className="row contact">
                    <DefaultTextField
                      name="fax"
                      value={number}
                      onChange={(event) => setNumber(event.target.value)}
                      label="Fax Number*"
                    />
                    {!props.hideContacts && (
                      <IconButton onClick={() => setSearchModal(true)} className="blue">
                        <Icon
                          src="magnifying-glass"
                          className="white"
                          alt="Search contacts"
                          showTooltip={true}
                        />
                      </IconButton>
                    )}
                  </div>
                  <span className="small-title mt-2">Cover Page Text</span>
                  <TextareaAutosize
                    cacheMeasurements
                    name="cover_page"
                    value={coverPage}
                    onChange={(event) => setCoverPage(event.target.value)}
                  />
                  <DefaultCheckbox
                    name="save_copy"
                    value={saveCopy.save}
                    onChange={() => setSaveCopy({ ...saveCopy, save: !saveCopy.save })}
                    style={{ display: 'inline-block' }}
                    text="Save copy to Health Records"
                  />
                  {saveCopy.save && (
                    <>
                      <DefaultTextField
                        name="title"
                        label="Title"
                        value={saveCopy.title}
                        onChange={(event) =>
                          setSaveCopy({
                            ...saveCopy,
                            title: event.target.value,
                          })
                        }
                      />
                      <DefaultSelectField
                        name="document_type"
                        label="Type"
                        value={saveCopy.document_type}
                        onChange={(event) =>
                          setSaveCopy({
                            ...saveCopy,
                            document_type: event.target.value,
                          })
                        }
                        inputs={DOCUMENT_TYPE_ARRAY}
                        style={{ width: '100%' }}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          )}
          <ProgressMessage show={loading} />
          {!loading && documents.length === 0 && prescriptions.length === 0 && (
            <NotFoundScreen patient={patient} />
          )}
          <ToggleSwitch
            className="attachments-toggle"
            option1={AttachmentsTab.add}
            element1={AttachmentsTab.add}
            option2={AttachmentsTab.reorder}
            element2={AttachmentsTab.reorder}
            onChange={setAttachmentsTab}
            toggled={attachmentsTab === AttachmentsTab.reorder}
          />
          {attachmentsTab === AttachmentsTab.reorder && (
            <div className="reorder-stack">
              <StackItemsView
                stack={selected}
                hideStatus
                describeItem={(doc) => doc.title}
                onMoveEnd={(list) => setSelected(list)}
                onDeleteItem={(doc) => setSelected((prev) => prev.filter((it) => it !== doc))}
                onClickedItem={setDocument}
              />
            </div>
          )}
          {attachmentsTab === AttachmentsTab.add && (
            <DocumentListByCategory
              onDocumentClicked={setDocument}
              receipts={receipts}
              documents={documents}
              prescriptions={prescriptions}
              loading={loading}
              showTotal={props.hideSend}
              isItemSelected={(value) => value.id === document?.id}
              isItemChecked={isDocumentSelected}
              renderBadge={
                props.hideSend
                  ? undefined
                  : (document) => (
                      <SelectableDocumentBadge
                        document={document}
                        isChecked={isDocumentSelected(document)}
                        onToggle={toggleDocument}
                        index={getDocumentIndex(document)}
                        showOrder={true}
                        patient={patient}
                      />
                    )
              }
            />
          )}
        </div>
        {document !== undefined && (
          <>
            <div className="details-wrapper full-height">
              {!!document?.filename && <DocumentDetails document={document} disableEdit={true} />}
              {!document?.filename && !!document?.attachments && (
                <PrescriptionDetails prescription={document} />
              )}
            </div>
            {!!document?.filename && (
              <LargeResourcePreview
                key={(document || {}).id}
                autoLoad={true}
                getFile={(dispatchOptions) =>
                  dispatch(
                    api.Documents.getDocumentFile({ documentId: document?.id }),
                    dispatchOptions,
                  )
                }
                getFileInfo={() =>
                  dispatch(
                    api.Documents.getDocumentFileInfo({
                      documentId: document?.id,
                    }),
                  )
                }
              />
            )}
            {!!document?.receipt_url && (
              <LargeResourcePreview
                key={(document || {}).id}
                autoLoad={true}
                getFile={(dispatchOptions) =>
                  dispatch(
                    api.Invoices.getReceiptPdf({
                      patientId: patient?.id,
                      receiptId: document.id,
                    }),
                    dispatchOptions,
                  )
                }
              />
            )}
          </>
        )}
      </div>
      {!props.hideSend && (
        <div className="bottom-content">
          <DefaultButton
            className="send-button"
            name={`Send ${option === OPTIONS.email ? 'Email' : 'Fax'} (${
              selected.length
            } attachments)`}
            disabled={disabledButton}
            loading={sending}
            onClick={send}
          />
        </div>
      )}
    </>
  );
};

const NotFoundScreen = (props) => (
  <div className="not-found">
    <span>No documents found for {PatientUtils.getName(props.patient)}</span>
  </div>
);

SendMultipleDocuments.propTypes = {
  patient: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  documents: PropTypes.array,
  hideToggle: PropTypes.bool,
  hideContacts: PropTypes.bool,
  hideSend: PropTypes.bool,
  emailFirst: PropTypes.bool,
  email: PropTypes.string,
  fax: PropTypes.string,
  emailSubject: PropTypes.string,
  emailBody: PropTypes.string,
};

export default SendMultipleDocuments;
